// ABOUTME: Renders the metadata-driven form for editing an ATS job.
// ABOUTME: Persists pipeline e-mails as records owned by the edited job.

import React from "react";
import Page from "Pages/_Layouts/DefaultInternal";
import useCompleoReactHookForm, {
    useCompleoReactHookFormGetMainData
} from "customHooks/useCompleoReactHookForm";
import useGetMetadata from "customHooks/useGetMetadata";
import { useTranslation } from "react-i18next";
import Loading from "customHooks/useCompleoReactHookForm/helpers/Loading";
import { useApi } from "customHooks/useApi";
import FormClassic from "customHooks/useCompleoReactHookForm/components/FormClassic";
import { useAuthState } from "_ReactContext/AuthContext";
import useNextPageFunctionsNewJob from "./components/useNextPageFunctionsNewJob";
import { useCancelButtonForm } from "customHooks/useCancelButtonForm";
import useValuesFromSource from "customHooks/useValuesFromSource";
import useCustomLists from "./components/useCustomLists";
import useHandleInitialValuesJobEdit from "./components/useHandleInitialValuesJobEdit";
import CustomComponents from "./components/CustomComponents";
import useAdditionalPropertiesAndFormMenu from "./components/useAdditionalPropertiesAndFormMenu";
import { formikReturnFN } from "./components/helper";
import useJobFieldsToHide from "./components/useJobFieldsToHide";
import { getHandleJobSubmit } from "./jobPostHelper";
import { useQueryStringRedirectAfter } from "customHooks/useQueryString/useQueryString";
import { Box } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CompleoErrorMessage from "Components/CompleoErrorMessage";
import { filterWhatsappApplicationMetadata } from "./components/whatsappApplicationMetadata";

const JobEdit = (props: any) => {
    const redirectAfter = useQueryStringRedirectAfter(props);
    const cancelFunction = useCancelButtonForm(redirectAfter || "/job/list");

    const moduleName = "JOBEDIT";
    const [t, i18n, readyTranslation] = useTranslation(moduleName, {
        useSuspense: false
    });

    const { company, timeZone } = useAuthState();

    const nextPageFunctions = useNextPageFunctionsNewJob(
        company.companyId,
        t,
        true
    );

    const postAddress = `/job/edit`;
    const [editJobReturn, editJob] = useApi(postAddress, "post");

    const postAddressSearch = `/job/search`;
    const [getJobSearchResult] = useApi(
        postAddressSearch,
        "post",
        { id: props.id, companyId: company.companyId, searchForEdit: true },
        false
    );
    const jobSearchDBData = getJobSearchResult.response?.data;
    const is404error = getJobSearchResult?.response?.response?.status === 404;

    const [listDefinition, readyLists] = useCustomLists(
        jobSearchDBData?.lists,
        t
    );

    const [metadata] = useGetMetadata(moduleName);
    const whatsappApplicationEnabled =
        company.whatsappEnabled === true &&
        company.whatsappApplicationEnabled === true;
    const filteredMetadata = React.useMemo(
        () =>
            filterWhatsappApplicationMetadata(
                metadata,
                whatsappApplicationEnabled
            ),
        [metadata, whatsappApplicationEnabled]
    );
    const [valuesFromSource] = useValuesFromSource(
        filteredMetadata,
        true,
        {},
        getJobSearchResult,
        ["data", "mainData"]
    );
    const {
        initialValues,
        translations,
        messages,
        languages,
        defaultLanguage,
        ready: readyInitialValues
    } = useHandleInitialValuesJobEdit(
        valuesFromSource,
        jobSearchDBData?.languageSettings
    );

    const { fieldsToHide, setFieldsToHide } = useJobFieldsToHide(languages);

    const finalValues: Compleo.IObject = {
        ...valuesFromSource,
        ...initialValues
    };
    finalValues.id = props.id;

    const addPropsAndFormMenu = useAdditionalPropertiesAndFormMenu(
        translations,
        messages,
        languages,
        defaultLanguage,
        readyInitialValues,
        t,
        props.id,
        jobSearchDBData?.applicantsByJobStage
    );
    const handleSubmit = getHandleJobSubmit(editJob, {
        emailPersistenceMode: "job-edit",
        persistPipelineState: (pipeline) => setValue("pipeline", pipeline)
    });

    const reactHookFormMethods = useCompleoReactHookFormGetMainData({
        t,
        ready: readyTranslation && readyInitialValues,
        i18nV: i18n,
        metadadosRetorno: filteredMetadata,
        valuesFromSource: finalValues,
        timeZone: timeZone
    });
    const { watch, setValue } = reactHookFormMethods.reactHookFormMethods;
    const enableScreeningQuestionnaire = watch("enableScreeningQuestionnaire");
    const openingReason = watch("openingReason");

    React.useEffect(() => {
        formikReturnFN(setValue, watch, fieldsToHide, setFieldsToHide);
    }, [enableScreeningQuestionnaire, openingReason, fieldsToHide]);

    // const formikReturn = React.useCallback(
    //     (value: any) => {
    //         // formikReturnFN(value, fieldsToHide, setFieldsToHide);
    //     },
    //     [fieldsToHide]
    // );

    const [formCompleo, finished] = useCompleoReactHookForm({
        t,
        ready:
            readyTranslation && readyInitialValues && addPropsAndFormMenu.ready,
        i18nV: i18n,
        postMethod: handleSubmit,
        postReturn: editJobReturn,
        redirectAddress: redirectAfter || "/job/list",
        FormType: FormClassic,
        metadadosRetorno: filteredMetadata,
        valuesFromSource: finalValues,
        timeZone: timeZone,
        nextPageFunctions: nextPageFunctions, //nextPageFunctions,
        secondaryButtonFunction: cancelFunction,
        customLists: listDefinition,
        CustomComponents: CustomComponents,
        FormMenuActions: addPropsAndFormMenu.FormMenuActions,
        additionalFieldProperties: [
            ...addPropsAndFormMenu.additionalProperties,
            {
                fieldName: "title",
                props: { isEditMode: true }
            }
        ],
        fieldsToHide: fieldsToHide,
        sizeToChangeStepHorizontal: "lg",
        enableStepNavigation: true,
        IsMultiStepLateralParam: true,
        invalidateQueryClientItems: ["/job/list", "/job/search"],
        removeQueryClientItems: ["JobViewJob"],
        reactHookFormMethods: reactHookFormMethods
    });

    let compRetorno: boolean | JSX.Element;
    compRetorno = <Loading />;

    if (is404error) {
        compRetorno = (
            <Box width="100%">
                <CompleoErrorMessage
                    errorTitle={t("notFound")}
                    errorDescription={t("notFoundMessage")}
                />
            </Box>
        );
    }

    const ready =
        readyLists && finished && readyTranslation && addPropsAndFormMenu.ready;

    if (ready) {
        compRetorno = formCompleo;
        return (
            <>
                <Page
                    featureName={t("a_FeatureName")}
                    featureMainDescription={t("a_FeatureMainDescription")}
                    maxWidth="lg"
                >
                    {compRetorno}
                </Page>
            </>
        );
    } else {
        return <Page>{compRetorno}</Page>;
    }
};

// JobNew.whyDidYouRender = true;
export default JobEdit;
